import { Pattern, Rank } from '../sim/defs';
import { Ev } from '../sim/events';
import { PickupKind } from '../sim/pools/PickupPool';
import type { World } from '../sim/World';
import { MUSIC_STEMS, renderStems, type StemId } from './music';
import { renderAll } from './synth';
import { Category, VoicePool } from './VoicePool';
import { SECONDARY_SOUND, SECONDARY_ULTI } from './weaponSounds';

/** Gain de mixage de chaque couche DANS le bus musique, et niveau d'intensité à
 *  partir duquel elle entre. Monter d'un cran ALLUME une couche de plus ; on ne
 *  change jamais de morceau, on empile. */
const STEM_MIX: Readonly<Record<StemId, { gain: number; from: number }>> = {
  pad: { gain: 0.9, from: 0 },
  bass: { gain: 0.8, from: 1 },
  arp: { gain: 0.65, from: 2 },
  drums: { gain: 0.85, from: 3 },
};

/* ═══════════════════════════════════════════════════════════════════════════
   Le graphe audio, et les pièges du navigateur.

       sources → busGain(music | sfx | ui) → masterGain → limiteur → sortie

   ⚠️ LE LIMITEUR N'EST PAS OPTIONNEL (PLAN.md §5.1).

   Dès qu'une quinzaine de sons se superposent, leur somme dépasse 0 dBFS et
   produit un clipping numérique très sale. C'est le défaut audio le plus
   fréquent des shmups amateurs, et le plus sournois : il n'apparaît qu'en fin
   de développement, quand la densité monte, c'est-à-dire quand on a cessé de
   se poser la question. Un seul DynamicsCompressorNode réglé en limiteur, pour
   tout le jeu, coût CPU négligeable.

   Deux quirks navigateur traités ici, et ils sont la raison pour laquelle
   Howler ne suffirait pas (il est de toute façon figé depuis septembre 2023) :

     1. AUTOPLAY — un AudioContext démarre suspendu. Il faut `resume()` ET la
        lecture d'un tampon silencieux DANS LE MÊME gestionnaire d'événement,
        sinon Safari le laisse suspendu sans rien dire.

     2. iOS — le contexte est suspendu au passage en arrière-plan et NE REVIENT
        JAMAIS SEUL (bug WebKit 237878). D'où le `resume()` systématique sur
        `visibilitychange`. Symptôme classique quand on l'oublie : « plus de son
        après avoir changé d'onglet », sans la moindre erreur console.
   ═══════════════════════════════════════════════════════════════════════════ */

export class AudioDirector {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private busSfx: GainNode | null = null;
  private busMusic: GainNode | null = null;
  private busUi: GainNode | null = null;

  private buffers = new Map<string, AudioBuffer>();

  /** Musique procédurale : tampons de couches, sources en boucle, état. */
  private musicBuffers: Map<StemId, AudioBuffer> | null = null;
  private musicSources: Array<{ stem: StemId; src: AudioBufferSourceNode; gain: GainNode }> = [];
  private musicOn = false;
  private musicLevel = 0;

  /** Boucle SFX courante (son d'arme en vol) : identifiant, source, gain. */
  private loopId: string | null = null;
  private loopSrc: AudioBufferSourceNode | null = null;
  private loopGain: GainNode | null = null;
  /** Volume musique VOULU par le joueur — base du ducking. Coder 0,6 en dur dans
   *  duck/restore écraserait le réglage du joueur à chaque coup encaissé. */
  private musicBase = 0.6;

  private readonly pool: VoicePool;
  /** Nœuds de gain des voix en cours, pour pouvoir les couper EN RAMPE. */
  private readonly voiceGains: Array<GainNode | null>;
  private readonly voiceSources: Array<AudioBufferSourceNode | null>;

  private unlocked = false;
  private detach: Array<() => void> = [];

  constructor(mobile = false) {
    // 32 voix sur desktop, 16 sur mobile : un plafond unique dimensionné
    // desktop fait craquer le CPU audio d'un téléphone.
    this.pool = new VoicePool(mobile ? 16 : 32);
    this.voiceGains = new Array<GainNode | null>(this.pool.size).fill(null);
    this.voiceSources = new Array<AudioBufferSourceNode | null>(this.pool.size).fill(null);
  }

  get ready(): boolean {
    return this.unlocked && this.ctx !== null;
  }

  /**
   * ⚠️ À appeler DEPUIS un gestionnaire d'événement utilisateur, et à RAPPELER
   * à chaque geste tant que `ready` est faux.
   *
   * Cette fonction est SYNCHRONE, et ce n'est pas un détail de style. Deux
   * régressions vécues l'imposent :
   *
   *  1. Tout `await` avant `resume()` rend la main à la boucle d'événements et
   *     peut faire expirer l'activation transitoire du geste utilisateur. Le
   *     navigateur refuse alors de démarrer le contexte, avec pour seul indice
   *     un avertissement console.
   *
   *  2. Le rendu des tampons — quelques centaines de milliers d'échantillons —
   *     doit venir APRÈS le déblocage, jamais avant. Le faire d'abord consomme
   *     assez de temps pour perdre la même activation.
   *
   * Elle est aussi IDEMPOTENTE et sûre à rappeler : un déblocage qui échoue
   * doit pouvoir être retenté au geste suivant, sinon la page reste
   * définitivement muette après un premier échec.
   */
  unlock(): boolean {
    if (this.unlocked) return true;

    // Contexte créé une seule fois, puis réutilisé entre deux tentatives.
    if (this.ctx) {
      this.kick();
      return this.finishIfRunning();
    }

    const ctx = new AudioContext({ latencyHint: 'interactive' });
    this.ctx = ctx;

    // `resume()` est asynchrone : `state` ne bascule pas forcément sur
    // « running » dans le même tour. Sans cet écouteur, le premier geste
    // renverrait faux et il faudrait appuyer deux fois pour avoir du son.
    ctx.addEventListener('statechange', () => {
      this.finishIfRunning();
    });

    // Un seul limiteur, sur le master, pour tout le jeu.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.ratio.value = 16;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;
    limiter.knee.value = 0;
    limiter.connect(ctx.destination);

    const master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(limiter);
    this.master = master;

    const mk = (v: number): GainNode => {
      const g = ctx.createGain();
      g.gain.value = v;
      g.connect(master);
      return g;
    };
    this.busSfx = mk(1);
    this.busMusic = mk(0.6);
    this.busUi = mk(0.8);

    // ⚠️ DÉBLOCAGE D'ABORD, travail lourd ensuite. Inverser les deux est
    // exactement ce qui faisait échouer le démarrage.
    this.kick();
    return this.finishIfRunning();
  }

  /** `resume()` + tampon silencieux d'un échantillon, dans le même tour de
   *  boucle que le geste. C'est ce tampon qui achève réellement le déblocage
   *  sur Safari — `resume()` seul ne suffit pas. */
  private kick(): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;

    void ctx.resume();

    const silent = ctx.createBufferSource();
    silent.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    silent.connect(master);
    silent.start(0);
  }

  /** Ne déclare le déblocage acquis QUE si le contexte tourne réellement.
   *  Se fier au seul appel à `resume()` est ce qui produit un jeu muet sans
   *  aucun message d'erreur. */
  private finishIfRunning(): boolean {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return false;

    // Rendu des tampons : lourd, et donc fait seulement maintenant — une fois
    // le contexte acquis, plus rien ne dépend de l'activation du geste.
    if (this.buffers.size === 0) this.buffers = renderAll(ctx);
    if (!this.musicBuffers) this.musicBuffers = renderStems(ctx);

    this.unlocked = true;
    this.attachLifecycle();
    // Les vrais bruitages (ElevenLabs) SE SUBSTITUENT aux placeholders quand ils
    // arrivent. Volontairement NON attendu : le déblocage reste synchrone (un
    // `await` ici perdrait l'activation du geste), et le jeu a déjà du son grâce
    // au synthé. S'il n'y a aucun asset, on garde le synthé, sans bruit.
    void this.loadRealSamples(ctx);
    return true;
  }

  /**
   * Remplace les tampons synthétisés par les échantillons RÉELS listés dans
   * `public/sfx/manifest.json` (produits par `tools/gen-sfx.mjs`). Chaque son
   * décodé écrase le placeholder de même `id` — d'où une transition invisible :
   * on entend le synthé, puis le vrai son dès qu'il est prêt. Aucune erreur
   * bloquante : un manifeste absent (assets non générés) est le cas nominal.
   */
  private async loadRealSamples(ctx: AudioContext): Promise<void> {
    let res: Response;
    try {
      res = await fetch('/sfx/manifest.json', { cache: 'no-cache' });
    } catch (e) {
      console.warn('[audio] manifeste des bruitages injoignable — on reste au synthé', e);
      return;
    }
    if (!res.ok) {
      console.info(`[audio] pas de bruitages réels (manifest HTTP ${res.status}) — synthé conservé`);
      return;
    }
    const manifest = (await res.json()) as { sounds?: Array<{ id: string; file: string }> };
    const sounds = manifest.sounds ?? [];
    let loaded = 0;
    await Promise.all(
      sounds.map(async ({ id, file }) => {
        try {
          const r = await fetch(`/sfx/${file}`, { cache: 'force-cache' });
          if (!r.ok) {
            console.warn(`[audio] ${file} → HTTP ${r.status}`);
            return;
          }
          const buf = await ctx.decodeAudioData(await r.arrayBuffer());
          this.buffers.set(id, buf); // substitution synthé → réel
          loaded++;
        } catch (e) {
          console.warn(`[audio] échec du chargement/décodage de ${file}`, e);
        }
      }),
    );
    console.info(`[audio] ${loaded}/${sounds.length} bruitages réels chargés`);
  }

  /** État du contexte, pour affichage. Un jeu muet doit être VISIBLE : c'est la
   *  panne la plus facile à ne pas remarquer pendant des semaines. */
  get state(): string {
    return this.ctx ? this.ctx.state : 'absent';
  }

  private attachLifecycle(): void {
    const onVisible = (): void => {
      // iOS ne réveille jamais le contexte tout seul (WebKit 237878).
      if (!document.hidden) void this.ctx?.resume();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    this.detach = [
      () => document.removeEventListener('visibilitychange', onVisible),
      () => window.removeEventListener('focus', onVisible),
    ];
  }

  /**
   * Joue un son. `panX` est la position à l'écran normalisée dans [-1, 1].
   *
   * La spatialisation utilise un StereoPannerNode borné à ±0,6, FIXÉ au
   * déclenchement. `PannerNode` en HRTF est proscrit : la convolution binaurale
   * par source est le nœud le plus coûteux du Web Audio, et la cause directe de
   * la dégradation progressive des FPS documentée sur `three.PositionalAudio`.
   */
  play(soundId: string, category: number, panX = 0, gain = 1): void {
    const ctx = this.ctx;
    const bus = category === Category.Ui ? this.busUi : this.busSfx;
    if (!ctx || !bus || !this.unlocked) return;

    const buf = this.buffers.get(soundId);
    if (!buf) return;

    const now = ctx.currentTime * 1000;
    // Durée RÉELLE du tampon (et non une valeur déclarée) : juste pour le pool de
    // voix, et surtout correct quand un vrai son ElevenLabs remplace le synthé
    // avec une durée différente.
    const d = this.pool.request(soundId, category, now, buf.duration * 1000, gain);
    if (d.slot < 0) return; // refusé : doublon, ou plafond de catégorie atteint

    // Voix volée : on la coupe EN RAMPE de 12 ms. Un `stop()` sec produirait un
    // clic audible — le genre de défaut qu'on entend sans savoir le nommer.
    if (d.stolen >= 0) this.fadeOut(d.stolen, 0.012);

    const src = ctx.createBufferSource();
    src.buffer = buf;
    // Léger detune par instance : sans lui, dix tirs identiques sonnent comme
    // un seul tir plus fort, pas comme dix tirs.
    src.detune.value = (Math.random() * 2 - 1) * 60;

    const g = ctx.createGain();
    const jitter = 1 + (Math.random() * 2 - 1) * 0.08; // ±1,5 dB environ
    g.gain.value = d.gain * jitter;

    const pan = ctx.createStereoPanner();
    pan.pan.value = Math.max(-0.6, Math.min(0.6, panX * 0.6));

    src.connect(g).connect(pan).connect(bus);
    src.start();

    this.voiceGains[d.slot] = g;
    this.voiceSources[d.slot] = src;
    src.onended = () => {
      if (this.voiceSources[d.slot] === src) {
        this.voiceSources[d.slot] = null;
        this.voiceGains[d.slot] = null;
      }
    };
  }

  private fadeOut(slot: number, seconds: number): void {
    const ctx = this.ctx;
    const g = this.voiceGains[slot];
    const src = this.voiceSources[slot];
    if (!ctx || !g || !src) return;
    const t = ctx.currentTime;
    g.gain.cancelScheduledValues(t);
    g.gain.setValueAtTime(g.gain.value, t);
    g.gain.linearRampToValueAtTime(0, t + seconds);
    try {
      src.stop(t + seconds);
    } catch {
      /* déjà arrêté */
    }
  }

  /**
   * Règle l'INTENSITÉ musicale (0 à 3) en fondant les couches. On n'échange
   * jamais de morceau — on empile : plus l'intensité monte, plus de stems
   * s'allument. Le fondu est lent (≈ 0,8 s) pour qu'une montée en combat ne
   * s'entende jamais comme un « coup » de musique.
   *
   * Démarre la musique à la volée si besoin : elle ne peut naître qu'après le
   * déblocage (les tampons sont rendus à ce moment), donc on l'amorce au premier
   * appel utile plutôt que de dépendre d'un ordre de boot.
   */
  setMusicIntensity(level: number): void {
    this.musicLevel = level < 0 ? 0 : level > 3 ? 3 : level;
    this.ensureMusic();
    const ctx = this.ctx;
    if (!ctx) return;
    for (const s of this.musicSources) {
      const mix = STEM_MIX[s.stem];
      const target = this.musicLevel >= mix.from ? mix.gain : 0;
      s.gain.gain.setTargetAtTime(target, ctx.currentTime, 0.8);
    }
  }

  /** Démarre les couches, une fois, si le contexte est prêt. Toutes les sources
   *  partent au MÊME instant et bouclent sur la même durée : elles restent donc
   *  calées à jamais, et allumer une couche revient à monter son gain, pas à la
   *  (re)lancer désynchronisée. */
  private ensureMusic(): void {
    const ctx = this.ctx;
    const bus = this.busMusic;
    if (this.musicOn || !this.unlocked || !ctx || !bus || !this.musicBuffers) return;

    const t0 = ctx.currentTime + 0.06;
    for (const stem of MUSIC_STEMS) {
      const buf = this.musicBuffers.get(stem);
      if (!buf) continue;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      const gain = ctx.createGain();
      gain.gain.value = 0; // les couches montent via setMusicIntensity
      src.connect(gain).connect(bus);
      src.start(t0);
      this.musicSources.push({ stem, src, gain });
    }
    this.musicOn = true;
  }

  /**
   * Boucle SFX unique (le son d'une arme dont un projectile est en vol). Une
   * seule à la fois — le joueur ne tire qu'une secondaire — donc `id` remplace la
   * boucle courante : on fond l'ancienne, on lance la nouvelle. `null` éteint.
   * Passer le même `id` deux fois est un no-op (appelé chaque frame par la vue).
   *
   * La source vit DIRECTEMENT sur le bus SFX, hors pool de voix : c'est un fond
   * continu, il ne doit ni voler une voix ni être volé par un tir.
   */
  setSfxLoop(id: string | null): void {
    const ctx = this.ctx;
    const bus = this.busSfx;
    if (!ctx || !bus || !this.unlocked) return;
    if (id === this.loopId) return;

    // Fond puis coupe la boucle courante (rampe = pas de clic).
    if (this.loopSrc && this.loopGain) {
      const g = this.loopGain;
      const s = this.loopSrc;
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
      try {
        s.stop(ctx.currentTime + 0.3);
      } catch {
        /* déjà arrêtée */
      }
      this.loopSrc = null;
      this.loopGain = null;
    }

    this.loopId = id;
    if (id === null) return;
    const buf = this.buffers.get(id);
    if (!buf) {
      this.loopId = null;
      return;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain).connect(bus);
    src.start();
    gain.gain.setTargetAtTime(0.6, ctx.currentTime, 0.12); // fondu d'entrée
    this.loopSrc = src;
    this.loopGain = gain;
  }

  /** Ducking de la musique. `setTargetAtTime` et jamais une affectation
   *  directe de `.value` : une discontinuité de gain produit un clic.
   *  `cancelScheduledValues` d'abord, sinon deux ducks se superposent.
   *  Relatif au volume VOULU (`musicBase`), jamais à un 0,6 en dur. */
  duckMusic(amountDb: number, attack = 0.05): void {
    const ctx = this.ctx;
    const bus = this.busMusic;
    if (!ctx || !bus) return;
    const target = this.musicBase * Math.pow(10, amountDb / 20);
    bus.gain.cancelScheduledValues(ctx.currentTime);
    bus.gain.setTargetAtTime(target, ctx.currentTime, attack);
  }

  restoreMusic(release = 0.4): void {
    const ctx = this.ctx;
    const bus = this.busMusic;
    if (!ctx || !bus) return;
    bus.gain.cancelScheduledValues(ctx.currentTime);
    bus.gain.setTargetAtTime(this.musicBase, ctx.currentTime, release);
  }

  setVolume(bus: 'master' | 'sfx' | 'music' | 'ui', v: number): void {
    // Le volume musique VOULU devient la nouvelle base du ducking.
    if (bus === 'music') this.musicBase = v;
    const node =
      bus === 'master' ? this.master : bus === 'sfx' ? this.busSfx : bus === 'music' ? this.busMusic : this.busUi;
    if (node && this.ctx) {
      node.gain.setTargetAtTime(v, this.ctx.currentTime, 0.02);
    }
  }

  /** Draine les événements de simulation et les traduit en sons.
   *  Table DÉCLARATIVE : ajouter un événement sonore ne demande de toucher à
   *  rien d'autre que ce `switch`. */
  consume(w: World, playfieldHalfWidth: number): void {
    if (!this.ready) return;
    const ev = w.events;
    for (let i = 0; i < ev.count; i++) {
      const px = ev.b(i) / playfieldHalfWidth;
      switch (ev.type(i)) {
        case Ev.EnemyKilled:
          this.play(ev.a(i) >= 2 ? 'explode_med' : 'explode_small', Category.Explosion, px);
          break;
        case Ev.PlayerHit:
          this.play('player_hit', Category.PlayerDamage, px, 1.2);
          this.duckMusic(-7);
          break;
        case Ev.DeathBombed:
          this.play('death_bomb', Category.Ui, 0, 1.1);
          break;
        case Ev.BombUsed:
          this.play('bomb', Category.Explosion, 0, 1.1);
          break;
        case Ev.Graze:
          this.play('graze', Category.Ui, px, 0.6);
          break;
        case Ev.PickupCollected:
          // Un POWER-UP (a >= Bomb) claque plus fort qu'un ramassage de monnaie :
          // c'est une récompense qu'on est allé chercher, pas un centime.
          if (ev.a(i) >= PickupKind.Bomb) this.play('powerup', Category.Pickup, px, 1);
          else this.play('pickup', Category.Pickup, px, 0.7);
          break;
        case Ev.PlayerDied:
          // La mort RÉELLE, pas l'annulation death-bomb : grave et sans appel.
          this.play('player_death', Category.PlayerDamage, px, 1.2);
          this.duckMusic(-9);
          break;
        case Ev.PlayerRespawned:
          this.play('respawn', Category.Ui, px, 0.9);
          this.restoreMusic();
          break;
        case Ev.ShieldBlocked:
          // Le bouclier a sauvé le coup : un tintement rassurant, pas une alarme.
          this.play('shield', Category.Ui, px, 0.9);
          break;
        case Ev.Overheat:
          // Le canon vient de saturer : l'alarme de surchauffe. Front unique par
          // épisode (hystérésis côté sim), donc pas de martèlement à craindre.
          this.play('overheat', Category.Ui, px, 1);
          break;
        case Ev.Jam:
          // Le canon s'est ENRAYÉ : clac + sifflement de vapeur. Front unique.
          this.play('jam', Category.Ui, px, 1);
          break;
        case Ev.SecondaryFired: {
          // Son PROPRE à l'arme secondaire déclenchée (repli générique). `a` est
          // l'index de la secondaire dans le catalogue.
          const sec = w.defs.secondaries[ev.a(i)];
          const sound = (sec && SECONDARY_SOUND[sec.id]) || 'secondary';
          this.play(sound, Category.PlayerSecondary, px, 0.8);
          break;
        }
        case Ev.ObstacleHit:
          // Le tir MORD la roche (sans la casser) : un « tok » discret, à la
          // cadence du tir. Volume bas — c'est une texture, pas un événement.
          this.play('impact', Category.Explosion, px, 0.5);
          break;
        case Ev.ObstacleBroken:
          // `d` porte le rayon : un gros astéroïde fait un fracas plus lourd.
          this.play(ev.d(i) >= 46 ? 'rock_big' : 'rock', Category.Explosion, px, 0.85);
          break;
        case Ev.WaveStarted:
          // Télégraphe d'arrivée, discret : « il en vient ». Au centre.
          this.play('warn', Category.Ui, 0, 0.8);
          break;
        case Ev.BossSpawned:
          // L'entrée du boss : grondement menaçant + on dégage la place musicale.
          this.play('boss_appear', Category.Explosion, 0, 1.1);
          this.duckMusic(-6);
          break;
        case Ev.BossPhase:
          // Bascule de phase : une alerte brève qui claque sur l'action.
          this.play('boss_phase', Category.Ui, px, 1);
          break;
        case Ev.BossKilled:
          // La chute du boss : sa fanfare, et la musique reprend toute sa place.
          this.play('boss_death', Category.Explosion, px, 1.2);
          this.restoreMusic();
          break;
        case Ev.UltiCharge:
          // La montée en puissance — on baisse la musique pour dégager la place.
          this.play('ulti_charge', Category.Explosion, 0, 1);
          this.duckMusic(-8);
          break;
        case Ev.UltiDischarge: {
          // La déflagration : le grand moment, PROPRE À L'ARME déchargée (le
          // faisceau orbital, la nuée…). Repli sur le blast générique. `a` =
          // index de la secondaire.
          const su = w.defs.secondaries[ev.a(i)];
          const sound = (su && SECONDARY_ULTI[su.id]) || 'ulti_blast';
          this.play(sound, Category.Explosion, 0, 1.2);
          this.restoreMusic();
          break;
        }
        case Ev.EnemyDamaged:
          // Impact sur un GROS ennemi seulement (rang porté par `a`) : un toc
          // charnu. Les petits ennemis se taisent — sinon chaque balle cognerait.
          if (ev.a(i) >= Rank.Heavy) this.play('hit_heavy', Category.EnemyShot, px, 0.5);
          break;
        case Ev.EnemyDive:
          // Le rugissement du piqué kamikaze.
          this.play('enemy_dive', Category.EnemyShot, px, 0.9);
          break;
        case Ev.EnemyFired: {
          // Une voix par tir ennemi, mais on ne SORT DU LOT que les patterns
          // marquants (laser, spirale, salve) ; le tir de base (visé/éventail)
          // reste une texture discrète. Le pool `EnemyShot` (cap 6) throttle la
          // densité en bullet-hell.
          const pat = ev.a(i);
          if (pat === Pattern.Beam) this.play('enemy_laser', Category.EnemyShot, px, 0.9);
          else if (pat === Pattern.Spiral || pat === Pattern.Homing)
            this.play('enemy_spiral', Category.EnemyShot, px, 0.8);
          else if (pat === Pattern.Burst || pat === Pattern.Wall || pat === Pattern.Ring)
            this.play('enemy_burst', Category.EnemyShot, px, 0.75);
          else this.play('enemy_shot', Category.EnemyShot, px, 0.4);
          break;
        }
        default:
          break;
      }
    }
  }

  get voices(): VoicePool {
    return this.pool;
  }

  dispose(): void {
    for (const d of this.detach) d();
    this.detach = [];
    // `ctx.close()` arrête déjà toutes les sources ; on remet juste l'état à
    // plat pour qu'une éventuelle réinitialisation reparte proprement.
    this.musicSources = [];
    this.musicOn = false;
    this.loopSrc = null;
    this.loopGain = null;
    this.loopId = null;
    void this.ctx?.close();
    this.ctx = null;
    this.unlocked = false;
  }
}
